import { ExpressedOutcomeEnum, format } from '@talos/kyoko';
import Big from 'big.js';

export const DEFAULT_PREDICTION_PRICE_INCREMENT = '0.01';

/** Same increment preference as market data cards: Default, then Min, then 0.01. */
export function getPredictionPriceIncrement(
  security?: { DefaultPriceIncrement?: string; MinPriceIncrement?: string } | null
): string {
  return security?.DefaultPriceIncrement || security?.MinPriceIncrement || DEFAULT_PREDICTION_PRICE_INCREMENT;
}

/** Formats chance % for display; rounds here so comparisons/sorting keep the raw value. */
export function formatPredictionOddsLabel(oddsPct: number | undefined): string {
  if (oddsPct == null || !Number.isFinite(oddsPct)) {
    return '—';
  }
  return `${Math.round(oddsPct)}%`;
}

function priceToDecimal(
  price: number | string | undefined,
  priceIncrement: string = DEFAULT_PREDICTION_PRICE_INCREMENT
): string | undefined {
  if (price == null || price === '') {
    return undefined;
  }
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice)) {
    return undefined;
  }
  const formatted = format(price, { pretty: false, round: true, spec: priceIncrement });
  return formatted === '' ? undefined : formatted;
}

function outcomeName(outcome: ExpressedOutcomeEnum): string {
  return outcome;
}

/** Chance percent shown on the gauge and outcome header: the Yes mid as a whole percent. */
export function getPredictionChancePct(yesMidPrice: number | string | undefined): number | undefined {
  if (yesMidPrice == null || yesMidPrice === '') {
    return undefined;
  }
  const yesMid = Number(yesMidPrice);
  return Number.isFinite(yesMid) ? Math.round(yesMid * 100) : undefined;
}

/** Formats a yes-book price using the security's DefaultPriceIncrement (e.g. 0.12). */
export function formatPredictionPriceLabel(
  price: number | string | undefined,
  priceIncrement: string = DEFAULT_PREDICTION_PRICE_INCREMENT
): string | undefined {
  return priceToDecimal(price, priceIncrement);
}

export function formatPredictionOutcomeButtonLabel(
  outcome: ExpressedOutcomeEnum,
  price: number | string | undefined,
  priceIncrement: string = DEFAULT_PREDICTION_PRICE_INCREMENT
): string {
  const priceLabel = formatPredictionPriceLabel(price, priceIncrement);
  return priceLabel == null ? outcomeName(outcome) : `${outcomeName(outcome)} ${priceLabel}`;
}

export function formatPredictionDecimalOutcomeButtonLabel(
  outcome: ExpressedOutcomeEnum,
  price: number | string | undefined,
  priceIncrement: string = DEFAULT_PREDICTION_PRICE_INCREMENT
): string {
  return formatPredictionOutcomeButtonLabel(outcome, price, priceIncrement);
}

/**
 * Yes/No button labels whose prices always sum to 1. The Yes and No mid prices are
 * complements, but rounding each independently can show e.g. 0.02 / 0.99. Anchor on Yes and
 * derive No = 1 - Yes so the displayed pair is always consistent.
 */
export function formatComplementaryPredictionOutcomeLabels(
  yesPrice: number | string | undefined,
  noPrice: number | string | undefined,
  priceIncrement: string = DEFAULT_PREDICTION_PRICE_INCREMENT
): { yes: string; no: string } {
  const yesDecimal = priceToDecimal(yesPrice, priceIncrement);
  if (yesDecimal == null || priceToDecimal(noPrice, priceIncrement) == null) {
    return {
      yes: formatPredictionOutcomeButtonLabel(ExpressedOutcomeEnum.Yes, yesPrice, priceIncrement),
      no: formatPredictionOutcomeButtonLabel(ExpressedOutcomeEnum.No, noPrice, priceIncrement),
    };
  }
  const noDecimal = format(Big(1).minus(yesDecimal), { pretty: false, round: true, spec: priceIncrement });
  return {
    yes: `${outcomeName(ExpressedOutcomeEnum.Yes)} ${yesDecimal}`,
    no: `${outcomeName(ExpressedOutcomeEnum.No)} ${noDecimal}`,
  };
}

export function getPredictionSubmitButtonLabel(
  outcome: ExpressedOutcomeEnum | undefined,
  isStaged: boolean
): string | undefined {
  if (!outcome) {
    return undefined;
  }
  const prefix = isStaged ? 'Stage' : 'Place';
  return `${prefix} ${outcome}`;
}
